/**
 * Created by fers on 05.02.16.
 */
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {FormattedMessage, injectIntl, defineMessages, IntlShape} from 'react-intl';

import types from '../../common/model-api/types';
import urlMapping from '../app/URLMapping';

import Editor from '../fw/Editor.react';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import FormFields from '../fw/FormFields.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import * as Permission from '../../common/util/Permission';
import * as BrowserPermission from '../BrowserPermission';
import {DEFAULT_CURRENCY} from '../../common/locales';
import {RadioGroup} from 'formsy-react-components';
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'organisationEdit.title', 'defaultMessage': 'Editing organisation'},
    billType: {id: 'organisationAdd.billType', 'defaultMessage': 'Billing type'},
    separete: {id: 'organisationAdd.separete', 'defaultMessage': 'I want to invoice separately'},
    together: {id: 'organisationAdd.together', 'defaultMessage': 'I want to invoice together'},
});

interface OrganisationEditProps {
    intl: IntlShape;
    user: User;
}

class OrganisationEdit extends React.Component<OrganisationEditProps> {

    static propTypes = {
        intl: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };
    typeName = 'organisations';

    constructor(props) {
        super(props);

        let billType = 'separate';
        let data = props.user.organisation;

        if (data.billedBy_id) {
            billType = 'together';
        }

        this.state = {
            billType: billType,
        };
    }

    render() {
        let data = this.props.user.organisation;
        let type = types[this.typeName];

        if (!data['billingCurrency']) {
            data['billingCurrency'] = DEFAULT_CURRENCY;
        }

        let orgsToBill = this.props.user.organisation.relatedOrganisations.edges.map(({node}) => node).filter((org) => org.realId !== this.props.user.organisation.realId && !org.billedBy_id);

        let consultants = this.props.user.organisation.instance.consultantUsers.edges.map(({node}) => node);


        let columns = ['name', 'description', 'email', 'phoneNumber', 'www', 'logo', 'billingName', 'billingEmail', 'billingCurrency', 'identNum', 'vatId', 'street', 'city', 'zip', 'country', 'billedBy', 'useInkaso', 'bankAccount'];
        let mainColumns = {'name': {}, 'description': {}};

		const isDeveloper = BrowserPermission.isDeveloper();
		const isPartner2 = BrowserPermission.isPartner2();
		const isPartner1 = BrowserPermission.isPartner1();
        if (isDeveloper) {
            columns.push("implementationDetails");
            columns.push("implementationDetailsLink");
            columns.push("consultant");
            columns.push("variableSymbol");
            columns.push("versionStrategy");
            columns.push("blocked");
            columns.push("blockedReason");
        }
        if (isPartner1) {
            columns.push("adminExtraEmail");
        }
        if (isDeveloper) {
            mainColumns["implementationDetails"] = {};
            mainColumns['implementationDetailsLink'] = {};
            mainColumns["adminExtraEmail"] = {};
            mainColumns['versionStrategy'] = {};
            mainColumns["consultant"] = {
                values: consultants,
                displayKey: 'name'
            };
            mainColumns['variableSymbol'] = {};
            mainColumns['blocked'] = {};
            mainColumns['blockedReason'] = {};
            mainColumns['blockedAt'] = {};
        }

        let billingRequired = false;
        let showBillingSelection = true;
        if (this.state.billType === 'separate') {
            billingRequired = true;
        }
        if (orgsToBill.length === 0) {
            showBillingSelection = false;
            billingRequired = true;
        }

        let billingColumns = {
            'billingName': {
                required: billingRequired,
            },
            'billingEmail': {
                required: billingRequired,
            },
            'identNum': {
                required: billingRequired,
            },
            'vatId': {
            },
            'street': {},
            'city': {},
            'zip': {},
            'country': {
                required: billingRequired,
            },
            'billingCurrency': {
                required: billingRequired,
            },
            'useInkaso': {},
            'bankAccount': {},
        };

        if (!billingRequired) {
            billingColumns = {
                'billedBy': {
                    values: orgsToBill,
                    displayKey: 'name'
                },
            };
        } else {
            if (isDeveloper) {
                columns.push("billingFrequency");
                billingColumns['billingFrequency'] = {};
                columns.push("reviewInvoice");
                billingColumns['reviewInvoice'] = {};
                columns.push("customerOrderNo");
                billingColumns['customerOrderNo'] = {};
                columns.push("customerInvoiceText");
                billingColumns['customerInvoiceText'] = {};
            }
        }

        return (
            <AppPanel breadcrumbParams={[
                {type, data}
            ]} title={this.props.intl.formatMessage(messages.title)}>
                <Editor
                        cancelable
                        columns={columns}
                        data={data}
                        disableAutoFields
                        fatQuery={Relay.QL `fragment on UpdateOrganisationPayload {
                            updatedNode {
                                id,
                                realId,
                                name,
                                billingName,
                                billingEmail,
                                billingCurrency,
                                implementationDetails,
                                implementationDetailsLink,
                                adminExtraEmail,
                                description,
                                billingFrequency,
                                reviewInvoice,
                                versionStrategy,
                                isBillable,
                                billingInfoFilled,
                                showMissingBillingInfo,
                                blocked,
                                blockedReason,
                                blockedAt,
                                customerOrderNo,
                                customerInvoiceText,
                                street,
                                city,
                                zip,
                                useInkaso,
                                variableSymbol,
                                consultant_id,
                                consultant {
                                    id,
                                },
                                bankAccount,
                                email,
                                www,
                                logo,
                                phoneNumber,
                                identNum,
                                vatId,
                                country,
                                billedBy {
                                    id,
                                },
                                billedBy_id,
                            }
                        }`}
                        metadata={type}
                        mutation={Relay.QL `mutation { updateOrganisation }`}
                        onBeforeSubmit={async (data) => {
                            if (this.state.billType === 'separate') {
                                data['billedBy'] = '';
                            }
                            return data;
                        }}
						organisationId={data.realId}
                        urlMapping={urlMapping}
                >
                    <FormFields columns={mainColumns}
                                data={data}
                                metadata={type}
                    />

                    <div className="bs-callout bs-callout-success">
                        <h4><FormattedMessage defaultMessage="Contact information (optional)" id="organisation.contact" /></h4>
                        <FormFields columns={['email', 'phoneNumber', 'www', 'logo']}
                                    data={data}
                                    metadata={type}
                        />
                    </div>

                    <div className="bs-callout bs-callout-default">
                        <h4><FormattedMessage defaultMessage="Billing information (required)" id="organisation.billingRequired" /></h4>
                        {showBillingSelection ? <div className="formGroup">
                            <RadioGroup
                                label={this.props.intl.formatMessage(messages.billType)}
                                name="billedSeparatedly"
                                onChange={(key, val) => {
                                    this.setState({billType: val});
                                }}
                                options={[
                                    {value: 'separate', label: this.props.intl.formatMessage(messages.separete)},
                                    {value: 'together', label: this.props.intl.formatMessage(messages.together)},
                                ]}
                                value={this.state.billType}
                            />
                        </div> : null}

                        <FormFields columns={billingColumns}
                                    data={data}
                                    metadata={type}
                        />

                        <p><FormattedMessage defaultMessage="You will be billed by invoice." id="organisation.billingNote" /></p>
                    </div>

                </Editor>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(OrganisationEdit), (props) => {
    return props.user.organisation;
}), {
    initialVariables: {
        organisationId: '',
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    billingName,
                    billingEmail,
                    billingCurrency,
                    blocked,
                    blockedReason,
                    blockedAt,
                    name,
                    description,
                    street,
                    city,
                    zip,
                    email,
                    phoneNumber,
                    consultant_id,
                    consultant {
                        id,
                        realId,
                    },
                    implementationDetails,
                    implementationDetailsLink,
                    adminExtraEmail,
                    billingFrequency,
                    reviewInvoice,
                    versionStrategy,
                    customerOrderNo,
                    customerInvoiceText,
                    www,
                    logo,
                    vatId,
                    country,
                    identNum,
                    useInkaso,
                    variableSymbol,
                    bankAccount,
                    billedBy_id,
                    billedBy {
                        id,
                        realId,
                        name,
                    },
                    relatedOrganisations(first: 500) {
                        edges {
                            node {
                                id,
                                realId,
                                name,
                                billedBy_id,
                            }
                        }
                    },
                    instance {
                        consultantUsers(first: 1000) {
                            edges {
                                node {
                                    id,
                                    realId,
                                    name,
                                    email,
                                    picture
                                }
                            }
                        }
                    }    
                }
            }
        `,
    },
});
